import type { TermlyConsentCategory, TermlyConsentState } from '../config/termly';

type ConsentListener = (state: TermlyConsentState | undefined) => void;

const consentListeners = new Set<ConsentListener>();
let consentSubscriptionStarted = false;

function getTermlyApi(): Window['Termly'] | undefined {
  return typeof window !== 'undefined' ? window.Termly : undefined;
}

export function readTermlyConsentState(): TermlyConsentState | undefined {
  const termly = getTermlyApi();
  if (!termly || typeof termly.getConsentState !== 'function') {
    return undefined;
  }

  return termly.getConsentState();
}

export function isTermlyCategoryConsented(category: TermlyConsentCategory): boolean {
  const state = readTermlyConsentState();
  return Boolean(state?.[category]);
}

function notifyConsentListeners(): void {
  const state = readTermlyConsentState();
  consentListeners.forEach((listener) => listener(state));
}

function attachTermlyConsentListeners(): void {
  const termly = getTermlyApi();
  if (!termly) {
    return;
  }

  notifyConsentListeners();

  if (typeof termly.on === 'function') {
    termly.on('consent', notifyConsentListeners);
  }
}

function ensureTermlyConsentSubscription(): void {
  if (consentSubscriptionStarted || typeof window === 'undefined') {
    return;
  }
  consentSubscriptionStarted = true;

  if (getTermlyApi()?.getConsentState) {
    attachTermlyConsentListeners();
    return;
  }

  const previousOnTermlyLoaded = window.onTermlyLoaded;
  window.onTermlyLoaded = () => {
    previousOnTermlyLoaded?.();
    attachTermlyConsentListeners();
  };

  const pollInterval = window.setInterval(() => {
    if (!getTermlyApi()?.getConsentState) {
      return;
    }

    window.clearInterval(pollInterval);
    attachTermlyConsentListeners();
  }, 100);

  window.setTimeout(() => window.clearInterval(pollInterval), 15_000);
}

export function subscribeToTermlyConsent(listener: ConsentListener): () => void {
  ensureTermlyConsentSubscription();
  consentListeners.add(listener);
  listener(readTermlyConsentState());

  return () => {
    consentListeners.delete(listener);
  };
}

/** @internal Resets module state between unit tests. */
export function __resetTermlyConsentForTests(): void {
  consentSubscriptionStarted = false;
  consentListeners.clear();
}
